var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Flex from "../../../components/templates/flex";
import useNavigationArrows from "../hooks/useNavigationArrows";
import useNavigationScroll from "../hooks/useNavigationScroll";
import useOnTabsResize from "../hooks/useOnTabsResize";
import Container from "./container";
import Arrow from "./arrow";
var DraggableTabs = function (_a) {
    var children = _a.children, dragEnd = _a.onDragEnd, onTabClose = _a.onTabClose, onResize = _a.onResize, collapsed = _a.collapsed;
    var ref = useRef();
    var tabsRef = useRef([]);
    useNavigationScroll(ref);
    var onTabsResize = useOnTabsResize(ref, tabsRef, onResize, [children]);
    var _b = useNavigationArrows(ref, tabsRef, children, collapsed), arrowLeft = _b[0], arrowRight = _b[1], onScroll = _b[2];
    var handlers = useCallback(function () {
        onTabsResize();
        onScroll();
    }, [collapsed]);
    useEffect(function () {
        if (!ref.current)
            return;
        var container = ref.current;
        handlers();
        container.addEventListener("scroll", onScroll);
        window.addEventListener("resize", handlers);
        return function () {
            container.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", handlers);
        };
    }, [children, collapsed]);
    var scrollLeft = function (e) {
        e.preventDefault();
        var container = ref.current;
        container.scrollTo({
            left: container.scrollLeft - 100,
            behavior: "smooth",
        });
    };
    var scrollRight = function (e) {
        e.preventDefault();
        var container = ref.current;
        container.scrollTo({
            left: container.scrollLeft + 100,
            behavior: "smooth",
        });
    };
    var setTabRef = useCallback(function (tab) {
        if (!tab)
            return;
        var tabs = tabsRef.current;
        if (children.length >= tabs.length)
            tabsRef.current = __spreadArrays(tabs, [tab]);
        if (children.length < tabs.length) {
            tabsRef.current = tabs.filter(function (node) {
                return node.getAttribute("data-rbd-draggable-id") === tab.getAttribute("data-rbd-draggable-id");
            });
        }
    }, [children]);
    var tabs = useMemo(function () {
        return React.Children.map(children, function (child, index) {
            var key = "tab-" + index;
            return (React.createElement(Draggable, { key: key, draggableId: key, index: index }, function (_a) {
                var innerRef = _a.innerRef, draggableProps = _a.draggableProps, dragHandleProps = _a.dragHandleProps;
                return React.cloneElement(child, __assign(__assign(__assign({}, draggableProps), { dragHandleProps: dragHandleProps, draggableRef: innerRef, tabRef: setTabRef, tabIndex: index, onClose: onTabClose }), child.props));
            }));
        });
    }, [children, onTabClose]);
    var onDragEnd = useCallback(function (result) {
        if (dragEnd)
            return dragEnd(result);
    }, [dragEnd]);
    return (React.createElement(DragDropContext, { onDragEnd: onDragEnd },
        React.createElement(Flex, { flex: "grow", basis: "0%", height: "100%", width: "100%", alignItems: "center", overflow: "hidden" },
            arrowLeft && React.createElement(Arrow, { onClick: scrollLeft, name: "navLeft" }),
            React.createElement(Droppable, { droppableId: "tabList", direction: "horizontal" }, function (_a) {
                var innerRef = _a.innerRef, placeholder = _a.placeholder, droppableProps = _a.droppableProps;
                return (React.createElement(Container, __assign({ ref: function (node) {
                        ref.current = node;
                        innerRef(node);
                    } }, droppableProps, { gap: 2, flex: "grow", basis: "0%", position: "relative" }),
                    tabs,
                    placeholder));
            }),
            arrowRight && React.createElement(Arrow, { onClick: scrollRight, name: "navRight" }))));
};
DraggableTabs.displayName = "DraggableTabs";
export default DraggableTabs;
